import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  Component,
  HostBinding,
  Input,
  OnInit,
  WritableSignal,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Validators as CustomValidators } from '../../core/validators/validator.constant';
import { VerificationTypeEnum } from '../../core/enums/verification-type.enum';
import { BaseWidget } from '../base-widget/base-widget';
import {
  AddressBook,
  AddressTypeEnum,
  LocationTypeTagEnum,
} from '@app/core/dto/address-book.dto';
import {
  ActionEnum,
  ActionRequestDto,
} from '@app/core/dto/abstract-action.dto';
import { LOGIN_MODE } from '@app/core/enums/login/login-mode.enum';
import { GlobalValidator } from '@app/core';
import { StringUtils } from '@app/core/utils/string.utils';
import { InputValidators } from '../utils/input-validation.util';
import { AddressInputEnum } from './enum/address-form-input-name.enum';
import { FormTypeEnum } from '@app/core/enums/form-type.enum';

@Component({
  selector: 'address-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgOptimizedImage],
  templateUrl: './address-form.component.html',
  styleUrl: './address-form.component.scss',
})
export class AddressFormComponent extends BaseWidget implements OnInit {
  @HostBinding('class') @Input() ngClass: any;
  @HostBinding('style') @Input() ngStyle: any;

  @Input() actionEvent: (event: any) => void;
  @Input() slot: any;
  data: any;

  addressForm: FormGroup;
  addressId: number;
  addressType: AddressTypeEnum;
  LoginType = LOGIN_MODE;

  isNewAddress = true;
  formChangesMade = false;

  verifiedGstin: WritableSignal<string> = signal('');
  verifiedPan: string = '';

  gstOptions = [];
  showGSTOptions = signal(false);

  pincodeData = signal<any>(null);

  showOTPVerification: WritableSignal<boolean> = signal(false);
  gstinVerified: WritableSignal<boolean> = signal(false);

  readOnly: WritableSignal<Record<string, boolean>> = signal({});
  isSignUp: WritableSignal<boolean> = signal(false);
  showSendOTPButton: WritableSignal<boolean> = signal(false);
  disableSaveAndContinue: WritableSignal<boolean> = signal(false);
  gstVerification: WritableSignal<boolean> = signal(false);
  hideError: WritableSignal<boolean> = signal(false);
  StringUtils = StringUtils;

  private readonly _inputValidators = inject(InputValidators, {
    optional: true,
  });

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
  ) {
    super();
  }

  blockPageReloadAndExitOnFormEdits() {
    this.addressForm.valueChanges.subscribe(() => {
      this.formChangesMade = true;
    });

    if (typeof window != 'undefined') {
      // WIP:: this shows a popup when changes are not saved
      /* window.onbeforeunload = (event) => {
        if (this.formChangesMade) {
          const confirmationMessage =
            'You have unsaved changes. Are you sure you want to leave?';
          (event || window.event).returnValue = confirmationMessage;
          return confirmationMessage;
        }
      }; */
    }
  }

  ngOnInit(): void {
    this.data = this.slot.widget.data.value;
    if (this.data?.formData?.gstin_verified) {
      this.gstVerification.set(true);
    }

    this.addressForm = this.formBuilder.group({
      name: [
        '',
        [Validators.required, Validators.pattern(GlobalValidator.NAME_REGEX)],
      ],
      phone: [
        '',
        [Validators.required, Validators.pattern(CustomValidators.PHONE_REGEX)],
      ],
      email: ['', [Validators.pattern(CustomValidators.EMAIL_REGEXP)]],
      alternatePhone: ['', [Validators.pattern(CustomValidators.PHONE_REGEX)]],
      gstin: ['', Validators.pattern(GlobalValidator.GSTIN_REGEX)],
      otp: [''],
      businessName: [''],
      pan: [''],
      pincode: [
        '',
        [
          Validators.required,
          Validators.pattern(CustomValidators.PINCODE_REGEX),
        ],
      ],
      address: [
        '',
        [
          Validators.required,
          Validators.maxLength(100),
          Validators.pattern(/\S/),
        ],
      ],
      address2: [''],
      city: new FormControl(
        {
          value: '',
          disabled: true,
        },
        [Validators.required],
      ),
      state: new FormControl(
        {
          value: '',
          disabled: true,
        },
        [Validators.required],
      ),
      landmark: [''],
      default: [true],
      sameShippingAddress: [true],
      sameBillingAddress: [true],
      businessVerificationOption: [VerificationTypeEnum.GST],
      locationTypeTag: [LocationTypeTagEnum.OFFICE],
    });

    if (this.data) {
      this.isNewAddress = false;
      // if req is from signup
      if (this.data?.signUp) {
        this.isNewAddress = true;
        this.data = this.data?.payload?.addressBook;

        this.readOnly.set({
          email: this.data?.email ? true : false,
          phone: this.data?.phone ? true : false,
          pincode: this.data?.pincode ? true : false,
          gstin: true,
        });
        this.isSignUp.set(true);
      }

      if (this.data?.type) {
        this.addressType = this.data?.type;
      } else {
        const url: string = this.router.url;
        if (url.split('/new/')[1]?.split('?')[0] === AddressTypeEnum.BILLING) {
          this.addressType = AddressTypeEnum.BILLING;
        } else {
          this.addressType = AddressTypeEnum.SHIPPING;
        }
      }
      this.addressId = Number(this.data?.id);

      this.verifiedGstin.set(this.data?.gstin_number);
      if (
        !StringUtils.isEmpty(this.data?.formData?.gstin_number) &&
        this.data?.formData?.gstin_verified
      ) {
        this.verifiedGstin.set(this.data?.formData?.gstin_number);
      }
      this.verifiedPan = this.data?.pan;
      if (this.data?.formData) {
        this.patchAddress(this.data?.formData);

        if (this.data?.otpData) {
          if (this.data?.otpData?.otpVerified) {
            this.addressForm.get(AddressInputEnum.OTP)?.setValue(null);
          } else {
            this.showOTPVerification.set(true);
            this.addressForm.patchValue({
              otp: this.data?.otpData?.otp,
            });
            setTimeout(() => {
              this.addressForm.get(AddressInputEnum.OTP)?.setErrors({
                error: this.data?.otpData?.otpStatusMsg,
              });
            });
          }
        }
      } else if (this.data?.id || this.isSignUp()) {
        this.patchAddress(this.data);
      } else if (
        !StringUtils.isEmpty(this.data?.profileEmail) &&
        StringUtils.isValidEmail(this.data?.profileEmail)
      ) {
        this.addressForm
          .get(AddressInputEnum.EMAIL)
          ?.setValue(this.data?.profileEmail);
      }
      this.addressForm.valueChanges.subscribe(() => {
        this.updateButtonStates();
      });
      this.updateButtonStates();
      if (this.data?.verificationStatus) {
        this.showSendOTPButton.set(false);
      }
      this.blockPageReloadAndExitOnFormEdits();
      return;
    } else {
      this.addressForm.valueChanges.subscribe(() => {
        this.updateButtonStates();
      });
      this.updateButtonStates();
      if (this.data?.verificationStatus) {
        this.showSendOTPButton.set(false);
      }
      this.blockPageReloadAndExitOnFormEdits();
    }

    const url: string = this.router.url;

    const addressType: string =
      url.split('/new/')[1]?.split('?')[0] || AddressTypeEnum.BILLING;

    switch (addressType) {
      case AddressTypeEnum.BILLING:
      case AddressTypeEnum.SHIPPING:
        this.addressType = addressType;
        break;
      default:
        if (window) {
          window.location.href = '/user/addressbook';
        }
    }
  }

  isInvalid(controlName: string) {
    const { invalid, touched } = this.addressForm.get(controlName) || {};
    return invalid && touched;
  }

  patchAddress(addressData: any) {
    if (
      typeof addressData === 'object' &&
      addressData &&
      Object.keys(addressData).length
    ) {
      this.addressForm.patchValue({
        address: addressData?.address,
        address2: addressData?.address2,
        businessName: addressData?.company_name,
        city: addressData?.city?.name,
        gstin: addressData?.gstin_number,
        default: addressData?.default,
        sameShippingAddress: addressData?.sameShippingAddress,
        sameBillingAddress: addressData?.sameBillingAddress,
        landmark: addressData?.landmark,
        name: addressData?.name,
        phone: addressData?.phone,
        email: StringUtils.isValidEmail(addressData?.email)
          ? addressData?.email
          : '',
        alternatePhone: addressData?.alternate_phone,
        pincode: addressData?.pincode,
        state: addressData?.state?.name,
        pan: addressData?.pan,
        locationTypeTag: addressData?.address_type,
      });

      if (
        !StringUtils.isEmpty(addressData.gstin_number) &&
        addressData.gstin_number ===
          this.addressForm.get(AddressInputEnum.GSTIN)?.value
      ) {
        this.gstinVerified.set(true);
      }
      this.toggleBusinessNameEnablement(addressData?.company_name);
      this.togglePincodeEnablement(addressData?.pincode);
      this.addressForm.markAllAsTouched();
      this.onPincodeChange();
    }
  }

  closeGSTOptions() {
    this.showGSTOptions.set(false);
  }

  getGSTfoFromPan() {
    const verificationBody = {
      type: VerificationTypeEnum.PAN,
      panNumber: this.addressForm.get(AddressInputEnum.PAN)?.value,
    };

    if (
      this.verifiedPan == this.addressForm.get(AddressInputEnum.PAN)?.value &&
      this.gstOptions.length
    ) {
      this.showGSTOptions.set(true);
      return;
    }

    this.apiService.callVerificationApi(verificationBody).subscribe({
      next: (res) => {
        const panResponseData = res.RESPONSE.data;

        if (panResponseData.status) {
          this.gstOptions = panResponseData.gstList;
          this.showGSTOptions.set(true);
          this.verifiedPan = panResponseData.pan;
        } else {
          this.addressForm.get(AddressInputEnum.PAN)?.setErrors({
            invalid: true,
          });
          this.gstOptions = [];
          this.showGSTOptions.set(true);
        }
      },
      error: () => {},
    });
  }

  onPincodeChange() {
    this.hideError.set(true);
    if (this.addressForm.get(AddressInputEnum.PINCODE)?.invalid) {
      return;
    }

    this.checkPincodeDetails();
  }

  async checkPincodeDetails() {
    return new Promise((resolve, reject) => {
      if (
        StringUtils.isEmpty(
          this.addressForm.get(AddressInputEnum.PINCODE)?.value,
        ) ||
        this.addressForm.get(AddressInputEnum.PINCODE)?.invalid
      ) {
        this.addressForm.get(AddressInputEnum.PINCODE)?.setErrors({
          invalid: true,
        });
        return;
      }

      const body: {
        type: VerificationTypeEnum;
        pincode: any;
      } = {
        type: VerificationTypeEnum.PINCODE,
        pincode: this.addressForm.get(AddressInputEnum.PINCODE)?.value,
      };

      this.apiService.callVerificationApi(body).subscribe({
        next: (res) => {
          const response = res.RESPONSE.data;

          if (!response.status) {
            this.addressForm.get(AddressInputEnum.PINCODE)?.setErrors({
              invalid: true,
            });
            this.addressForm.get(AddressInputEnum.PINCODE)?.setErrors({
              invalidPincodeError: 'The provided pincode is not serviceable.',
            });
            reject(response.message);
            return;
          }

          this.addressForm.patchValue({
            city: response?.city?.name,
            state: response?.state?.name,
          });

          this.pincodeData.set(response);

          resolve(response);
        },
        error: () => {},
      });
    });
  }

  async selectOption(option: any) {
    this.addressForm.patchValue({
      gstin: option.gstin,
    });

    this.verifyGST()
      .then((response) => {
        this.showGSTOptions.set(false);
      })
      .catch((error) => {});
  }

  verifyGST() {
    return new Promise((resolve, reject) => {
      const gstNumber: string = this.addressForm.get(
        AddressInputEnum.GSTIN,
      )?.value;

      if (!GlobalValidator.GSTIN_REGEX.test(gstNumber)) {
        this.addressForm.get(AddressInputEnum.GSTIN)?.setErrors({
          invalidGstinError: 'Enter a valid GSTIN number.',
        });
        return;
      }

      const verificationBody = {
        type: VerificationTypeEnum.GST,
        gstNumber: gstNumber,
        payload: {
          formType: FormTypeEnum.ADDRESS_FORM,
          formData: this.addressForm.value,
          pageUrl: this.router.url,
        },
      };

      this.apiService.callVerificationApi(verificationBody).subscribe({
        next: (res) => {
          const response = res.RESPONSE.data;
          if (!response.status && response.apiError) {
            this.addressForm.get(AddressInputEnum.GSTIN)?.setErrors({
              invalidGstinError:
                response.message || 'Enter a valid GSTIN number.',
            });
            this.verifiedGstin.set('');
            this.gstinVerified.set(false);
            this.gstVerification.set(false);
            reject(response.message);
            return;
          } else if (!response.status) {
            this.addressForm.get(AddressInputEnum.GSTIN)?.setErrors({
              invalidGstinError: response.message,
            });
            this.verifiedGstin.set('');
            this.gstinVerified.set(false);
            this.gstVerification.set(false);
            reject(response.message);
            return;
          }

          const verifiedGstNumber: string = response.gst || gstNumber;

          this.addressForm.patchValue({
            businessName: response.businessName,
            pincode: response.pincode,
            city: response?.city?.name,
            state: response?.state?.name,
            gstin: verifiedGstNumber,
            address: response.splitAddress?.[0] ?? response?.address,
            address2: response.splitAddress?.[1] ?? '',
          });

          if (response.status) {
            this.checkPincodeDetails();
            this.gstVerification.set(true);
          } else {
            this.addressForm.get(AddressInputEnum.GSTIN)?.setErrors({
              invalidGstinError: response.message,
            });
            this.gstVerification.set(false);
          }
          this.gstinVerified.set(true);

          this.verifiedGstin.set(verifiedGstNumber);
          this.togglePincodeEnablement(response.pincode);
          this.toggleBusinessNameEnablement(response?.businessName);

          resolve(response);
        },
        error: () => {},
      });
    });
  }

  async saveAddress() {
    this.addressForm.markAllAsTouched();
    if (
      this.data.loginType == LOGIN_MODE.EMAIL &&
      StringUtils.isValidEmail(this.data?.profileEmail) &&
      StringUtils.isEmpty(this.addressForm.get(AddressInputEnum.EMAIL)?.value)
    ) {
      this.addressForm
        .get(AddressInputEnum.EMAIL)
        ?.setValue(this.data?.profileEmail);
    }
    if (this.addressForm.invalid) {
      return;
    }

    // call action to save address
    const addressBook: Partial<AddressBook> = this.makeAddressData();

    const action: {
      actionData: {
        type: ActionEnum;
        params: any;
      };
    } = {
      actionData: {
        type: ActionEnum.ADDRESS_SAVE,
        params: {
          ...this.slot?.widget?.data?.value?.payload,
          addressBook,
          addressId: null,
          sameShippingAddress: this.addressForm.get(
            AddressInputEnum.SAME_SHIPPING_ADDRESS,
          )?.value,
          sameBillingAddress: this.addressForm.get(
            AddressInputEnum.SAME_BILLING_ADDRESS,
          )?.value,
        },
      },
    };

    if (this.addressId) {
      action.actionData.type = ActionEnum.ADDRESS_UPDATE;
      action.actionData.params.addressId = Number(this.addressId);
    }

    this.actionEvent(action);
  }

  async sendOTP() {
    this.showOTPVerification.set(true);
    this.actionEvent({
      actionData: {
        type: ActionEnum.SEND_OTP,
        params: {
          phone: this.addressForm.get(AddressInputEnum.PHONE)?.value,
        },
      },
    });
  }

  async verifyOTP() {
    const addressBook: Partial<AddressBook> = this.makeAddressData();
    this.actionEvent({
      actionData: {
        type: ActionEnum.VERIFY_OTP,
        params: {
          ...addressBook,
          phone: this.addressForm.get(AddressInputEnum.PHONE)?.value,
          otp: this.addressForm.get(AddressInputEnum.OTP)?.value,
          sameShippingAddress: this.addressForm.get(
            AddressInputEnum.SAME_SHIPPING_ADDRESS,
          )?.value,
          sameBillingAddress: this.addressForm.get(
            AddressInputEnum.SAME_BILLING_ADDRESS,
          )?.value,
        },
      },
    });
    this.showOTPVerification.set(false);
  }

  makeAddressData(): Partial<AddressBook> {
    const name =
      this.addressForm.get(AddressInputEnum.NAME)?.value?.split(' ') || [];
    const first_name = name[0];
    const last_name = name.length > 1 ? name[name.length - 1] : '';

    return {
      address: this.addressForm.get(AddressInputEnum.ADDRESS)?.value,
      address2: this.addressForm.get(AddressInputEnum.ADDRESS2)?.value,
      city_id: this.pincodeData()?.city?.id,
      state_id: this.pincodeData()?.state?.id,
      city: this.pincodeData()?.city?.name,
      state: this.pincodeData()?.state?.name,
      company_name: this.addressForm.get(AddressInputEnum.BUSINESS_NAME)?.value,
      name: this.addressForm.get(AddressInputEnum.NAME)?.value,
      first_name,
      last_name,
      gstin_number: this.addressForm.get(AddressInputEnum.GSTIN)?.value,
      landmark: this.addressForm.get(AddressInputEnum.LAND_MARK)?.value,
      default: this.addressForm.get(AddressInputEnum.DEFAULT)?.value,
      phone: Number(this.addressForm.get(AddressInputEnum.PHONE)?.value),
      email: this.addressForm.get(AddressInputEnum.EMAIL)?.value,
      alternate_phone:
        Number(this.addressForm.get(AddressInputEnum.ALTERNATE_PHONE)?.value) ||
        null,
      pincode: this.addressForm.get(AddressInputEnum.PINCODE)?.value,
      type: this.addressType,
      address_type: this.addressForm.get(AddressInputEnum.LOCATION_TYPE_TAG)
        ?.value,
      gstin_verified:
        !StringUtils.isEmpty(
          this.addressForm.get(AddressInputEnum.GSTIN)?.value,
        ) && this.gstVerification(),
    };
  }

  verifiedGstinNumber() {
    const gstinControl = this.addressForm.get(AddressInputEnum.GSTIN);
    const gstin: string = gstinControl?.value?.trim() ?? '';
    const wasSuccessfullyVerified: boolean = this.gstVerification();

    if (StringUtils.isEmpty(gstin)) {
      this.verifiedGstin.set('');
      this.gstinVerified.set(false);
      this.gstVerification.set(false);
      gstinControl?.setErrors(null);
      gstinControl?.updateValueAndValidity({ emitEvent: false });

      this.addressForm.get(AddressInputEnum.BUSINESS_NAME)?.setValue(null);
      this.addressForm.get(AddressInputEnum.PINCODE)?.setValue('');
      this.addressForm.get(AddressInputEnum.CITY)?.setValue('');
      this.addressForm.get(AddressInputEnum.STATE)?.setValue('');

      this.toggleBusinessNameEnablement(null);
      this.togglePincodeEnablement(
        this.addressForm.get(AddressInputEnum.PINCODE)?.value,
      );
      this.updateButtonStates();
      return;
    }

    if (
      this.verifiedGstin() !== gstin &&
      this.gstinVerified()
    ) {
      this.gstinVerified.set(false);
      this.gstVerification.set(false);
      this.addressForm.get(AddressInputEnum.BUSINESS_NAME)?.setValue(null);
      this.toggleBusinessNameEnablement(null);

      if (wasSuccessfullyVerified) {
        this.addressForm.get(AddressInputEnum.PINCODE)?.setValue('');
        this.addressForm.get(AddressInputEnum.CITY)?.setValue('');
        this.addressForm.get(AddressInputEnum.STATE)?.setValue('');
      }
    }
    this.togglePincodeEnablement(
      this.addressForm.get(AddressInputEnum.PINCODE)?.value,
    );
    this.updateButtonStates();
  }

  isPhoneNumberValid(): boolean {
    const phone = this.addressForm.get(AddressInputEnum.PHONE)?.value;
    return phone && GlobalValidator.PHONE_REGEX.test(phone);
  }

  onPhoneInputChange(event: any) {
    event.target.value = event?.target?.value.replace(/\D/g, '');
    const phone: string = this.addressForm.get(AddressInputEnum.PHONE)?.value;
    const isPhoneChanged: boolean = phone != this.data?.addressPhone;
    this.showOTPVerification.set(false);
    this.addressForm.get(AddressInputEnum.OTP)?.setValue('');

    this.showSendOTPButton.set(!this.data?.profilePhone && isPhoneChanged);
    this.disableSaveAndContinue.set(
      (!this.data?.profilePhone && isPhoneChanged) ||
        this.addressForm?.invalid ||
        this.showOTPVerification(),
    );
  }

  onInputChange(event: any) {
    event.target.value = event?.target?.value.replace(/\D/g, '');
  }

  updateButtonStates() {
    const phone: string = this.addressForm.get(AddressInputEnum.PHONE)?.value;
    const isPhoneChanged: boolean = phone != this.data.addressPhone;

    if (this.data?.profilePhone) {
      this.showSendOTPButton.set(false);
      this.disableSaveAndContinue.set(
        this.addressForm?.invalid || this.showOTPVerification(),
      );
    } else if (
      this.data?.profileEmail &&
      !this.data?.profilePhone &&
      this.data?.addressPhone !=
        this.addressForm.get(AddressInputEnum.PHONE)?.value
    ) {
      if (!this.data.verificationStatus) {
        this.showSendOTPButton.set(true);
      }
      this.disableSaveAndContinue.set(
        this.addressForm?.invalid || this.showOTPVerification(),
      );
    } else if (!this.data?.profilePhone && isPhoneChanged) {
      this.showSendOTPButton.set(true);
      this.disableSaveAndContinue.set(true);
    } else {
      this.showSendOTPButton.set(false);
      this.disableSaveAndContinue.set(
        this.addressForm?.invalid || this.showOTPVerification(),
      );
    }
  }

  performAction(action: ActionRequestDto) {
    this.actionEvent({
      actionData: {
        ...action,
      },
    });
  }

  isButtonDisabled(): boolean {
    if (this.isSignUp()) {
      return false;
    } else {
      return (
        this.disableSaveAndContinue() ||
        (this.addressForm.get(AddressInputEnum.GSTIN)?.value &&
          !this.gstinVerified()) ||
        (this.addressForm.get(AddressInputEnum.GSTIN)?.value &&
          this.gstinVerified() &&
          StringUtils.isEmpty(
            this.addressForm.get(AddressInputEnum.BUSINESS_NAME)?.value,
          )) ||
        (!this.data?.verificationStatus &&
          this.addressForm.get(AddressInputEnum.PHONE)?.value &&
          !this.isPhoneVerified())
      );
    }
  }

  isPhoneVerified() {
    return (
      this.data?.profilePhone ||
      this.data?.addressPhone ||
      this.data?.otpData?.otpVerified
    );
  }

  togglePincodeEnablement(pincode) {
    if (this._inputValidators?.checkPincode(pincode) && this.gstinVerified()) {
      this.addressForm.get(AddressInputEnum.PINCODE)?.disable();
    } else {
      this.addressForm.get(AddressInputEnum.PINCODE)?.enable();
    }
  }

  toggleBusinessNameEnablement(businessName: string | null) {
    if (StringUtils.isEmpty(businessName) && this.gstinVerified()) {
      this.addressForm.get(AddressInputEnum.BUSINESS_NAME)?.enable();
      this.addressForm
        .get(AddressInputEnum.BUSINESS_NAME)
        ?.setValidators([Validators.required]);
      this.addressForm
        .get(AddressInputEnum.BUSINESS_NAME)
        ?.updateValueAndValidity();
    } else {
      this.addressForm.get(AddressInputEnum.BUSINESS_NAME)?.disable();
      this.addressForm.get(AddressInputEnum.BUSINESS_NAME)?.clearValidators();
      this.addressForm
        .get(AddressInputEnum.BUSINESS_NAME)
        ?.updateValueAndValidity();
    }
  }

  shouldShowGstinError(): boolean {
    const gstinControl = this.addressForm.get('gstin');
    return (
      (gstinControl?.touched && gstinControl.errors != null) ||
      !!gstinControl?.errors?.invalidGstinError
    );
  }

  shouldShowPincodeError(): boolean {
    const pincodeControl = this.addressForm.get('pincode');
    return (
      (pincodeControl?.touched && pincodeControl.errors != null) ||
      !!pincodeControl?.errors?.invalidPincodeError
    );
  }
}
